import React, { Component } from 'react'
import { t, Trans } from '@lingui/macro'

import {
  ThemeModelAsMember,
  TopicVersionType
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'

import { Modal } from '../../../components/common/modal/Modal'
import { ButtonPrimary, ButtonClean, Select } from 'front-end/src/components'
import Form, { FormRow } from '../../../components/common/form/Form'
import Input from '../../../components/common/form/input/Input'
import Textarea from '../../../components/common/form/textarea/Textarea'
import Label from '../../../components/common/form/label/Label'
import uniqueId from '../../../components/utils/uniqueId'
import HorizontalChoiceSelector from '../../../components/common/horizontal-choice-selector/HorizontalChoiceSelector'
import singlePage from 'public/assets/img/single-page.svg'
import multiPage from 'public/assets/img/multi-page.svg'
import scorm from 'public/assets/img/scorm.svg'
import bongoIndividual from 'public/assets/img/bongo-individual.svg'
import bongoGroup from 'public/assets/img/bongo-group.svg'
import bongoQAndA from 'public/assets/img/bongo-q-and-a.svg'
import bongoInteractive from 'public/assets/img/bongo-interactive.svg'

import './CreateResourceModal.less'

import { useCreateTopicMutation } from './CreateResourceModal.codegen'
import { useWorkspaceManageTopicsAndThemesQuery } from '../resources-subpage/workspaceManageTopicsAndThemes.codegen'

const initialState = {
  title: '',
  introduction: '',
  isLoading: false,
  seriesId: 0,
  step: 1,
  resourceType: TopicVersionType.s
}

export default class CreateResourceModal extends Component<
  {
    workspaceId: number
    series: Array<ThemeModelAsMember>
    resolve: (resource: any) => void
    reject: () => void
  },
  {
    title: string
    introduction: string
    isLoading: boolean
    seriesId: number
    step: number
    resourceType: TopicVersionType
  }
> {
  inputTitleId = uniqueId()
  inputIntroductionId = uniqueId()
  inputThemeId = uniqueId()

  state = { ...initialState }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!nextProps.show) {
      this.setState({
        ...initialState
      })
    }
  }

  createResourceStepOne() {
    const { reject } = this.props
    const series: {
      value: number
      label: string
    }[] = [{ value: 0, label: t`None` }]
    if (this.props.series) {
      this.props.series.map((serie) => {
        series.push({ value: serie.id, label: serie.title as string })
      })
    }
    return (
      <Modal
        onHide={reject}
        header={t`Create a new resource`}
        controls={
          <>
            <ButtonClean onClick={reject}>
              <Trans>Cancel</Trans>
            </ButtonClean>
            <ButtonPrimary
              className="Button__next"
              onClick={() => {
                this.setState({
                  step: 2
                })
              }}
            >
              <Trans>Next</Trans>
            </ButtonPrimary>
          </>
        }
      >
        <Form>
          <FormRow>
            <Label htmlFor={this.inputTitleId}>
              <Trans>Title</Trans>
            </Label>
            <Input
              autoFocus
              className="Input__topicName"
              id={this.inputTitleId}
              fullWidth
              placeholder={t`What question will this answer?`}
              onChange={(val) => {
                this.setState({ title: val })
              }}
              value={this.state.title}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor={this.inputIntroductionId}>
              <Trans>Introduction</Trans>
            </Label>
            <Textarea
              id={this.inputIntroductionId}
              fullWidth
              placeholder={t`Describe how this resource will be useful`}
              onChange={(val) => {
                this.setState({ introduction: val })
              }}
              value={this.state.introduction}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor={this.inputThemeId} title={t`Theme`} />
            <Select
              id={this.inputThemeId}
              options={series}
              onChange={(v) => {
                this.setState({ seriesId: parseInt(v?.value, 10) })
              }}
            />
          </FormRow>
        </Form>
      </Modal>
    )
  }

  render() {
    const { step } = this.state
    return (
      <>
        {step === 1 && this.createResourceStepOne()}
        {step === 2 && (
          <CreateResourceStepTwo {...this.props} modalInstance={this} />
        )}
        {step === 3 && (
          <CreateResourceStepThree {...this.props} modalInstance={this} />
        )}
      </>
    )
  }
}

const CreateResourceStepThree = (props) => {
  const { reject, modalInstance } = props
  const { state } = modalInstance
  const workspaceId = props.workspaceId
  const { refetch } = useWorkspaceManageTopicsAndThemesQuery({
    variables: {
      workspaceId
    }
  })

  const [createTopic, { loading }] = useCreateTopicFromState(workspaceId, state)

  const options = [
    {
      value: TopicVersionType.VIDEO_ASSESSMENT_INDIVIDUAL,
      header: (
        <div className="file-icon bongoIndividual">
          <img src={bongoIndividual} alt={bongoIndividual} />
        </div>
      ),
      description: (
        <div className="description">
          <h3>
            <Trans>Individual video assessment</Trans>
          </h3>
          <span>
            <Trans>
              Users build communication and critical thinking skills as they
              continually practice, self-assess, and re-record video
              presentations.
            </Trans>
          </span>
        </div>
      )
    },
    {
      value: TopicVersionType.VIDEO_ASSESSMENT_GROUP,
      header: (
        <div className="file-icon bongoGroup">
          <img src={bongoGroup} alt={bongoGroup} />
        </div>
      ),
      description: (
        <div className="description">
          <h3>
            <Trans>Group video assessment</Trans>
          </h3>
          <span>
            <Trans>
              Users collaborate synchronously or asynchronously to complete a
              group activity.
            </Trans>
          </span>
        </div>
      )
    },
    {
      value: TopicVersionType.VIDEO_ASSESSMENT_Q_AND_A,
      header: (
        <div className="file-icon bongoQAndA">
          <img src={bongoQAndA} alt={bongoQAndA} />
        </div>
      ),
      description: (
        <div className="description">
          <h3>
            <Trans>Q&A video assessment</Trans>
          </h3>
          <span>
            <Trans>
              Users are put on the spot to answer questions asynchronously with
              only a limited time to respond.
            </Trans>
          </span>
        </div>
      )
    },
    {
      value: TopicVersionType.VIDEO_ASSESSMENT_INTERACTIVE,
      header: (
        <div className="file-icon bongoInteractive">
          <img src={bongoInteractive} alt={bongoInteractive} />
        </div>
      ),
      description: (
        <div className="description">
          <h3>
            <Trans>Interactive video assessment</Trans>
          </h3>
          <span>
            <Trans>
              Users answer multiple choice questions or submit video responses
              at different points throughout a video.
            </Trans>
          </span>
        </div>
      )
    }
  ]

  return (
    <Modal
      className={'CreateResourceModal__chooseResourceType'}
      onHide={reject}
      header={t`Choose a video assessment type:`}
      controls={
        <>
          <ButtonClean onClick={reject}>
            <Trans>Cancel</Trans>
          </ButtonClean>
          <ButtonPrimary
            className="Button__create"
            onClick={async () => {
              const res = await createTopic()

              refetch()

              props.resolve(res.data?.workspace?.createTopic)
            }}
            isLoading={loading}
            disabled={!state.resourceType}
          >
            <Trans>Create</Trans>
          </ButtonPrimary>
        </>
      }
    >
      <Form>
        <FormRow>
          <HorizontalChoiceSelector
            name="type"
            onChange={(val) => {
              modalInstance.setState({ resourceType: val })
            }}
            options={options}
            value={state.resourceType}
          />
        </FormRow>
      </Form>
    </Modal>
  )
}

const CreateResourceStepTwo = (props) => {
  const { reject, modalInstance } = props
  const { state } = modalInstance
  const workspaceId = props.workspaceId
  const { refetch } = useWorkspaceManageTopicsAndThemesQuery({
    variables: {
      workspaceId
    }
  })
  const { featureFlags } = useFeatureFlags()

  const [createTopic] = useCreateTopicFromState(workspaceId, state)

  const options = [
    {
      value: TopicVersionType.s,
      header: (
        <div className="file-icon single-page">
          <img src={singlePage} alt={singlePage} />
        </div>
      ),
      description: (
        <div className="description">
          <h3>
            <Trans>Single page</Trans>
          </h3>
          <span>
            <Trans>
              Keep it simple with the single page format and allow your readers
              to scroll through your resource at their own pace to find the
              information they need.
            </Trans>
          </span>
        </div>
      )
    },
    {
      value: TopicVersionType.m,
      header: (
        <div className="file-icon multi-page">
          <img src={multiPage} alt={multiPage} />
        </div>
      ),
      description: (
        <div className="description">
          <h3>
            <Trans>Slideshow</Trans>
          </h3>
          <span>
            <Trans>
              Give your readers a classic 'e-learning' presentation with our
              slideshow resource format. Perfect for quizzing and compliance
              training.
            </Trans>
          </span>
        </div>
      )
    },
    {
      value: TopicVersionType.scorm,
      header: (
        <div className="file-icon scorm">
          <img src={scorm} alt={scorm} />
        </div>
      ),
      description: (
        <div className="description">
          <h3>SCORM / AICC / xAPI / cmi5</h3>
          <span>
            <Trans>
              Import a package in any of the SCORM, AICC, xAPI, cmi5 and LTI
              standards to use as the content for this resource. Progress
              assessment and completion will be tracked in the same way as the
              content built directly in Looop.
            </Trans>
          </span>
        </div>
      )
    },
    {
      value: TopicVersionType.VIDEO_ASSESSMENT_INDIVIDUAL,
      header: (
        <div className="file-icon bongoVideoAssignment">
          <img src={bongoInteractive} alt={bongoInteractive} />
        </div>
      ),
      description: (
        <div className="description">
          <h3>
            <Trans>Video assessment</Trans>
          </h3>
          <span>
            <Trans>
              These structured workflows help evaluators facilitate spaced
              practice, skill demonstrations, peer-to-peer collaboration, and
              the application of knowledge within a real-world environment. Once
              users complete a video assignment, they submit their recording for
              personalised feedback and coaching.
            </Trans>
          </span>
        </div>
      )
    }
  ]

  if (!featureFlags?.SCORM) {
    options.splice(
      options.findIndex((i) => i.value === TopicVersionType.scorm),
      1
    )
  }

  if (!featureFlags?.bongo) {
    options.splice(
      options.findIndex(
        (i) => i.value === TopicVersionType.VIDEO_ASSESSMENT_INDIVIDUAL
      ),
      1
    )
  }

  return (
    <Modal
      className={'CreateResourceModal__chooseResourceType'}
      onHide={reject}
      header={t`Choose a resource type:`}
      controls={
        <>
          <ButtonClean onClick={reject}>
            <Trans>Cancel</Trans>
          </ButtonClean>
          <ButtonPrimary
            className="Button__create"
            onClick={async () => {
              if (
                state.resourceType ===
                TopicVersionType.VIDEO_ASSESSMENT_INDIVIDUAL
              ) {
                modalInstance.setState({
                  step: 3
                })
              } else {
                const res = await createTopic()
                refetch()

                props.resolve(res.data?.workspace?.createTopic)
              }
            }}
            isLoading={state.isLoading}
            disabled={!state.resourceType}
          >
            {state.resourceType === TopicVersionType.VIDEO_ASSESSMENT_INDIVIDUAL
              ? t`Next`
              : t`Create`}
          </ButtonPrimary>
        </>
      }
    >
      <Form>
        <FormRow>
          <HorizontalChoiceSelector
            name="type"
            onChange={(val) => {
              modalInstance.setState({ resourceType: val })
            }}
            options={options}
            value={state.resourceType}
          />
        </FormRow>
      </Form>
    </Modal>
  )
}
function useCreateTopicFromState(
  workspaceId: number,
  state: any
): [any, { loading: any }] {
  return useCreateTopicMutation({
    variables: {
      id: workspaceId,
      title: state.title,
      summary: state.introduction,
      type: state.resourceType,
      themeId: +state.seriesId || null
    }
  })
}
